import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { RHFAutoSelect, RHFTextField, RHFSelect } from '../../../components/hook-form/index';
import { getInstrumentList } from '../../../redux/slices/trades';
import axios from '../../../utils/axios';
import modifyScriptName from '../../../utils/modifyScriptName';

const orderTypeOptions = [
  { label: 'MARKET', value: 'MARKET' },
  { label: 'LIMIT', value: 'LIMIT' },
];
const sideOptions = [
  { label: 'BUY', value: 'BUY' },
  { label: 'SELL', value: 'SELL' },
];

// Validation schema using yup
const schema = yup.object().shape({
  script: yup.object().typeError('Script Selection is Required').required('Script selection is required'),
  orderType: yup.string().required('Order Type is required'),
  side: yup.string().required('Side is required'),
  quantity: yup
    .number()
    .typeError('Quantity is required')
    .required('Quantity is required')
    .min(0.1, 'Quantity must be at least 0.1')
    .test('multiple-of-0.1', 'Quantity must be a multiple of 0.1', (value) => {
      if (!value) return false;
      return Math.abs((value * 10) % 1) < 0.001; // Check if value * 10 is a whole number
    }),
  price: yup
    .number()
    .typeError('Price is required')
    .required('Price is required')
    .min(0.01, 'Price must be at least 0.01'),
});

const AddManualTradeModal = ({ open, client, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  // Fetch instrument data
  const { instrumentData } = useSelector((state) => state.trade);

  useEffect(() => {
    dispatch(getInstrumentList());
  }, [dispatch]);

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      script: null, // Initially no script is selected
      orderType: '',
      side: '',
      quantity: '',
      price: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  // State to manage the warning popup visibility and messages
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessages, setWarningMessages] = useState([]);

  // Reset form fields when modal closes
  useEffect(() => {
    if (!open) {
      reset({
        script: null,
        orderType: '',
        side: '',
        quantity: '',
        price: '',
      });
    }
  }, [open, reset]);

  // Handle the submission of the form
  const onSubmit = async (data) => {
    const { orderType, script, side, quantity, price } = data;
    try {
      const res = await axios.post('/trade/manual/add', {
        orderType,
        scriptId: script._id,
        side,
        price,
        quantity,
        userId: client._id,
      });
      // Check for a warning in the response
      if (res.data?.error?.hasWarning) {
        setWarningMessages(res.data.error.warnings);
        setShowWarning(true);
      }
      enqueueSnackbar(res.data.message, { variant: 'success' });
      reset();
      // onUserUpdate();
      // onClose();
    } catch (error) {
      if (error?.hasWarning) {
        setWarningMessages(error.warnings);
        setShowWarning(true);
      }
      enqueueSnackbar(`Failed to add trade: ${error.message}`, { variant: 'error' });
    }
  };

  // Handle the confirmation of the warning
  const handleWarningConfirm = async () => {
    const { orderType, script, side, quantity, price } = methods.getValues();
    try {
      setLoading(true);
      const res = await axios.post('/trade/manual/add', {
        orderType,
        scriptId: script._id,
        side,
        price,
        quantity,
        userId: client._id,
        force: true, // Include the force flag to override warnings
      });
      enqueueSnackbar(res.data.message, { variant: 'success' });
      setShowWarning(false);
      setLoading(false);
      reset();
      // onUserUpdate();
      // onClose();
    } catch (error) {
      enqueueSnackbar(`Failed to add trade: ${error.message}`, { variant: 'error' });
      setLoading(false);
    }
  };

  // Handle cancellation of the warning popup
  const handleWarningCancel = () => {
    setShowWarning(false);
  };

  // Extract and filter instrument data
  const instrumentDocs = instrumentData?.docs || [];

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h4" textAlign="center" pb={2}>
            Manual Trades & Limits
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
              <CircularProgress />
            </Box>
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <RHFTextField name="client" label="Client" value={client.username || ''} disabled fullWidth />
                  </Grid>
                  <Grid item xs={6}>
                    <RHFAutoSelect
                      name="script"
                      control={methods.control}
                      options={instrumentDocs}
                      labelField="tradingsymbol"
                      secondaryFieldPath="exchange.name"
                      formatLabel={modifyScriptName}
                      label="Script"
                      placeholder="Select Script"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <RHFSelect name="orderType" label="Order Type" fullWidth>
                      <option value="" />
                      {orderTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </RHFSelect>
                  </Grid>
                  <Grid item xs={6}>
                    <RHFSelect name="side" label="Side" fullWidth>
                      <option value="" />
                      {sideOptions.map((side) => (
                        <option key={side.value} value={side.value}>
                          {side.label}
                        </option>
                      ))}
                    </RHFSelect>
                  </Grid>
                  <Grid item xs={6}>
                    <RHFTextField name="quantity" label="Quantity" type="number" fullWidth />
                  </Grid>
                  <Grid item xs={6}>
                    <RHFTextField name="price" label="Price" type="number" fullWidth />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      type="submit"
                      fullWidth
                      size="large"
                      sx={{ textTransform: 'uppercase' }}
                      loading={isSubmitting}
                    >
                      Execute Order
                    </LoadingButton>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          )}
        </Box>
      </Modal>

      <Dialog open={showWarning}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          {warningMessages.map((message, index) => (
            <Typography key={index} sx={{ mb: 1 }}>
              {message}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningCancel} color="secondary">
            Cancel
          </Button>
          <LoadingButton onClick={handleWarningConfirm} color="primary" loading={loading} variant="contained">
            OK
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

AddManualTradeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  client: PropTypes.object.isRequired, // Client details from the row
  onClose: PropTypes.func.isRequired,
  onUserUpdate: PropTypes.func,
};

export default AddManualTradeModal;
